import React from "react";
import {
  Box,
  Button,
  Card,
  FormControl,
  FormLabel,
  Stack,
  Textarea,
  Typography,
  Input,
} from "@mui/joy";

import { AdminAnnouncement } from "../../api";

interface Props {
  initial?: Pick<AdminAnnouncement, "title" | "content">;
  onSubmit: (data: Pick<AdminAnnouncement, "title" | "content">) => Promise<void> | void;
  submitLabel?: string;
  title?: string;
  helperText?: string;
  onCancel?: () => void;
  isSubmitting?: boolean;
}

const EMPTY_FORM = { title: "", content: "" };
const MAX_CONTENT_LENGTH = 1200;

export default function AdminAnnouncementForm({
  initial,
  onSubmit,
  submitLabel = "Save announcement",
  title = "Compose announcement",
  helperText = "Share uplifting seva updates and encourage devotees to participate.",
  onCancel,
  isSubmitting = false,
}: Props) {
  const [form, setForm] = React.useState(() => initial ?? EMPTY_FORM);
  const [localSubmitting, setLocalSubmitting] = React.useState(false);

  React.useEffect(() => {
    setForm(initial ?? EMPTY_FORM);
  }, [initial]);

  const submitting = isSubmitting || localSubmitting;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === "content" ? value.slice(0, MAX_CONTENT_LENGTH) : value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      setLocalSubmitting(true);
      await onSubmit({ title: form.title.trim(), content: form.content.trim() });
    } finally {
      setLocalSubmitting(false);
    }
  };

  return (
    <Card
      component="form"
      onSubmit={handleSubmit}
      variant="outlined"
      sx={{
        borderRadius: "xl",
        p: { xs: 2.5, md: 3 },
        display: "flex",
        flexDirection: "column",
        gap: 2.5,
      }}
    >
      <Stack spacing={0.75}>
        <Typography level="title-lg" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Typography level="body-sm" sx={{ color: "text.secondary", maxWidth: 520 }}>
          {helperText}
        </Typography>
      </Stack>

      <Stack spacing={2}>
        <FormControl required>
          <FormLabel>Announcement title</FormLabel>
          <Input
            name="title"
            value={form.title}
            onChange={handleChange}
            placeholder="E.g. Ekadashi seva reminders"
            size="md"
            autoComplete="off"
          />
        </FormControl>
        <FormControl required>
          <FormLabel>Announcement message</FormLabel>
          <Textarea
            name="content"
            value={form.content}
            onChange={handleChange}
            minRows={6}
            maxRows={10}
            placeholder="Share the full devotional message, seva opportunities, and heartfelt gratitude."
            endDecorator={
              <Box sx={{ ml: "auto", color: "text.tertiary", fontSize: "var(--joy-fontSize-xs)" }}>
                {form.content.length}/{MAX_CONTENT_LENGTH} characters
              </Box>
            }
            sx={{
              borderRadius: "lg",
            }}
          />
        </FormControl>
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="flex-end">
        {onCancel ? (
          <Button
            type="button"
            variant="plain"
            color="neutral"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </Button>
        ) : null}
        <Button type="submit" loading={submitting} disabled={!form.title.trim() || !form.content.trim()}>
          {submitLabel}
        </Button>
      </Stack>
    </Card>
  );
}
